import exifr from 'exifr';

export interface ExifData {
  dateTimeOriginal: Date | null;
  make: string | null;
  model: string | null;
  gpsLatitude: number | null;
  gpsLongitude: number | null;
  temperature: number | null;
  imageWidth: number | null;
  imageHeight: number | null;
  rawExif: Record<string, unknown>;
}

// Patterns for temperature in UserComment (Reconyx/Browning formats)
const TEMP_PATTERNS = [
  /[Tt]emp(?:erature)?[: ]*(-?\d+(?:\.\d+)?)\s*[°]?[Ff]/,
  /[Tt]emp(?:erature)?[: ]*(-?\d+(?:\.\d+)?)/,
];

function parseTemperature(userComment: unknown): number | null {
  if (!userComment) return null;
  const str = String(userComment);
  for (const pattern of TEMP_PATTERNS) {
    const match = str.match(pattern);
    if (match?.[1] !== undefined) {
      const val = parseFloat(match[1]);
      return isNaN(val) ? null : val;
    }
  }
  return null;
}

function parseRawExif(raw: Record<string, unknown>): ExifData {
  const dateTimeOriginal =
    raw['DateTimeOriginal'] instanceof Date
      ? raw['DateTimeOriginal']
      : typeof raw['DateTimeOriginal'] === 'string'
        ? new Date(raw['DateTimeOriginal'] as string)
        : null;

  const make = typeof raw['Make'] === 'string' ? (raw['Make'] as string).trim() || null : null;
  const model = typeof raw['Model'] === 'string' ? (raw['Model'] as string).trim() || null : null;

  // exifr converts GPS DMS→decimal automatically when GPS tag group is requested
  const gpsLatitude = typeof raw['latitude'] === 'number' ? (raw['latitude'] as number) : null;
  const gpsLongitude = typeof raw['longitude'] === 'number' ? (raw['longitude'] as number) : null;

  const temperature = parseTemperature(raw['UserComment']);

  const imageWidth =
    typeof raw['ImageWidth'] === 'number'
      ? (raw['ImageWidth'] as number)
      : typeof raw['ExifImageWidth'] === 'number'
        ? (raw['ExifImageWidth'] as number)
        : null;

  const imageHeight =
    typeof raw['ImageHeight'] === 'number'
      ? (raw['ImageHeight'] as number)
      : typeof raw['ExifImageHeight'] === 'number'
        ? (raw['ExifImageHeight'] as number)
        : null;

  return {
    dateTimeOriginal: dateTimeOriginal instanceof Date && !isNaN(dateTimeOriginal.getTime()) ? dateTimeOriginal : null,
    make,
    model,
    gpsLatitude,
    gpsLongitude,
    temperature,
    imageWidth,
    imageHeight,
    rawExif: raw,
  };
}

const EMPTY_EXIF: ExifData = {
  dateTimeOriginal: null,
  make: null,
  model: null,
  gpsLatitude: null,
  gpsLongitude: null,
  temperature: null,
  imageWidth: null,
  imageHeight: null,
  rawExif: {},
};

const EXIFR_OPTIONS = {
  // Include GPS to get decimal lat/lng from exifr
  gps: true,
  tiff: true,
  exif: true,
  iptc: false,
  icc: false,
  // Translate GPS DMS to decimal degrees
  translateValues: true,
  // Expose lat/longitude at top-level
  mergeOutput: true,
};

export async function extractExifFromFile(file: File): Promise<ExifData> {
  try {
    // CAST: exifr.parse returns unknown; we know the shape from EXIFR_OPTIONS
    const raw = (await exifr.parse(file, EXIFR_OPTIONS)) as Record<string, unknown> | null | undefined;
    if (!raw) return { ...EMPTY_EXIF };
    return parseRawExif(raw);
  } catch {
    return { ...EMPTY_EXIF };
  }
}

export async function extractExifFromBlob(blob: Blob): Promise<ExifData> {
  try {
    // CAST: same as above
    const raw = (await exifr.parse(blob, EXIFR_OPTIONS)) as Record<string, unknown> | null | undefined;
    if (!raw) return { ...EMPTY_EXIF };
    return parseRawExif(raw);
  } catch {
    return { ...EMPTY_EXIF };
  }
}
